import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Block, Inline, parseMarkdown } from './markdown';
import { HelpArticleMeta, findArticleBySlug, helpArticlePath, resolveArticleLink } from './helpCenter';
import { cn } from '../../utils/utils';

interface MarkdownViewProps {
    source: string;
    /** Slug of the article being shown — relative links resolve against it. */
    slug: string;
    /** Drop the leading `# Titel` (the page renders the title itself). */
    skipTitle?: boolean;
    /** When set, article links call this instead of routing (the drawer keeps
     *  the reader in place). */
    onOpenArticle?: (slug: string, anchor?: string) => void;
    /** Element whose descendants carry the heading ids (for anchor scrolling). */
    scrollRoot?: React.RefObject<HTMLElement>;
    /** Articles this user may not see are linked as plain text. */
    isVisible?: (article: HelpArticleMeta) => boolean;
}

type RenderContext = Pick<MarkdownViewProps, 'slug' | 'onOpenArticle' | 'scrollRoot' | 'isVisible'>;

const linkClass = 'text-primary underline underline-offset-2 hover:opacity-80';

export const scrollToAnchor = (anchor: string, root?: HTMLElement | null) => {
    // Anchors are `[\w-]+` (parser and README convention), so the attribute
    // selector needs no escaping; ids starting with a digit stay valid here.
    if (!/^[\w-]+$/.test(anchor)) return;
    const scope: ParentNode = root ?? document;
    const target = scope.querySelector(`[id="${anchor}"]`);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// ── Inline ──────────────────────────────────────────────────────────────

const renderLink = (node: Extract<Inline, { type: 'link' }>, key: string, ctx: RenderContext): React.ReactElement => {
    const resolved = resolveArticleLink(ctx.slug, node.href);
    const targetArticle = resolved.kind === 'article' ? findArticleBySlug(resolved.slug) : undefined;
    const hiddenFromViewer = !!targetArticle && !!ctx.isVisible && !ctx.isVisible(targetArticle);
    const target = hiddenFromViewer ? ({ kind: 'unavailable' } as const) : resolved;
    const children = renderInline(node.children, key, ctx);
    switch (target.kind) {
        case 'external':
            return (
                <a key={key} className={linkClass} href={target.href} target={'_blank'} rel={'noopener noreferrer'}>
                    {children}
                </a>
            );
        case 'anchor':
            return (
                <a
                    key={key}
                    className={linkClass}
                    href={`#${target.anchor}`}
                    onClick={(event) => {
                        event.preventDefault();
                        scrollToAnchor(target.anchor, ctx.scrollRoot?.current);
                    }}
                >
                    {children}
                </a>
            );
        case 'article': {
            const href = helpArticlePath(target.slug, target.anchor);
            const { onOpenArticle } = ctx;
            if (onOpenArticle) {
                return (
                    <a
                        key={key}
                        className={linkClass}
                        href={href}
                        onClick={(event) => {
                            event.preventDefault();
                            onOpenArticle(target.slug, target.anchor);
                        }}
                    >
                        {children}
                    </a>
                );
            }
            return (
                <Link key={key} className={linkClass} to={href}>
                    {children}
                </Link>
            );
        }
        default:
            // Not shipped (hidden) or unknown: keep the words, drop the link.
            return <React.Fragment key={key}>{children}</React.Fragment>;
    }
};

const renderInlineNode = (node: Inline, key: string, ctx: RenderContext): React.ReactElement => {
    switch (node.type) {
        case 'text':
            return <React.Fragment key={key}>{node.text}</React.Fragment>;
        case 'code':
            return (
                <code key={key} className={'rounded bg-foreground/5 px-1 py-0.5 text-[0.85em]'}>
                    {node.text}
                </code>
            );
        case 'strong':
            return (
                <strong key={key} className={'font-semibold text-foreground'}>
                    {renderInline(node.children, key, ctx)}
                </strong>
            );
        case 'em':
            return <em key={key}>{renderInline(node.children, key, ctx)}</em>;
        case 'link':
            return renderLink(node, key, ctx);
        default:
            return <React.Fragment key={key} />;
    }
};

const renderInline = (nodes: Inline[], keyPrefix: string, ctx: RenderContext): React.ReactElement[] =>
    nodes.map((node, i) => renderInlineNode(node, `${keyPrefix}-${i}`, ctx));

// ── Blocks ──────────────────────────────────────────────────────────────

const renderHeading = (block: Extract<Block, { type: 'heading' }>, key: string, ctx: RenderContext) => {
    const content = renderInline(block.children, key, ctx);
    if (block.level <= 2) {
        return (
            <h2 key={key} id={block.id} className={'text-lg font-semibold text-foreground mt-8 mb-3 scroll-mt-24'}>
                {content}
            </h2>
        );
    }
    return (
        <h3 key={key} id={block.id} className={'text-base font-semibold text-foreground mt-6 mb-2 scroll-mt-24'}>
            {content}
        </h3>
    );
};

const renderList = (block: Extract<Block, { type: 'list' }>, key: string, ctx: RenderContext, tight: boolean) => {
    const items = block.items.map((item, j) => {
        const itemKey = `${key}-${j}`;
        const only = item.blocks.length === 1 ? item.blocks[0] : null;
        return (
            <li key={itemKey} className={'text-sm leading-6 text-foreground/80 pl-1'}>
                {only?.type === 'paragraph' ? renderInline(only.children, itemKey, ctx) : renderBlocks(item.blocks, itemKey, ctx, true)}
            </li>
        );
    });
    const spacing = cn('pl-5 space-y-1', tight ? 'mb-1' : 'mb-3');
    return block.ordered ? (
        <ol key={key} start={block.start} className={cn('list-decimal', spacing)}>
            {items}
        </ol>
    ) : (
        <ul key={key} className={cn('list-disc', spacing)}>
            {items}
        </ul>
    );
};

const renderRow = (cells: Inline[][], rowKey: string, ctx: RenderContext) => (
    <tr key={rowKey} className={'border-b border-foreground/5'}>
        {cells.map((cell, j) => (
            <td key={`${rowKey}-${j}`} className={'py-2 pr-4 align-top text-foreground/80'}>
                {renderInline(cell, `${rowKey}-${j}`, ctx)}
            </td>
        ))}
    </tr>
);

const renderTable = (block: Extract<Block, { type: 'table' }>, key: string, ctx: RenderContext) => (
    <div key={key} className={'overflow-x-auto mb-4'}>
        <table className={'w-full text-sm'}>
            <thead>
                <tr className={'border-b border-foreground/10'}>
                    {block.header.map((cell, j) => (
                        <th key={`${key}-h${j}`} className={'text-left py-2 pr-4 text-foreground/60 font-medium align-bottom'}>
                            {renderInline(cell, `${key}-h${j}`, ctx)}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>{block.rows.map((row, r) => renderRow(row, `${key}-r${r}`, ctx))}</tbody>
        </table>
    </div>
);

const renderBlock = (block: Block, key: string, ctx: RenderContext, tight: boolean): React.ReactElement => {
    switch (block.type) {
        case 'heading':
            return renderHeading(block, key, ctx);
        case 'paragraph':
            return (
                <p key={key} className={cn('text-sm leading-6 text-foreground/80', tight ? 'mb-1' : 'mb-3')}>
                    {renderInline(block.children, key, ctx)}
                </p>
            );
        case 'list':
            return renderList(block, key, ctx, tight);
        case 'table':
            return renderTable(block, key, ctx);
        case 'blockquote':
            return (
                <blockquote key={key} className={'border-l-4 border-foreground/20 bg-foreground/5 rounded-r px-4 pt-3 pb-1 mb-4'}>
                    {renderBlocks(block.blocks, key, ctx, false)}
                </blockquote>
            );
        case 'code':
            return (
                <pre key={key} className={'bg-foreground/5 rounded p-3 mb-4 overflow-x-auto text-xs leading-5 text-foreground/80'}>
                    {block.text}
                </pre>
            );
        case 'hr':
            return <hr key={key} className={'my-6 border-foreground/10'} />;
        default:
            return <React.Fragment key={key} />;
    }
};

const renderBlocks = (blocks: Block[], keyPrefix: string, ctx: RenderContext, tight: boolean): React.ReactElement[] =>
    blocks.map((block, i) => renderBlock(block, `${keyPrefix}-${i}`, ctx, tight));

const MarkdownView = ({ source, slug, skipTitle, onOpenArticle, scrollRoot, isVisible }: MarkdownViewProps) => {
    const blocks = useMemo(() => {
        const parsed = parseMarkdown(source);
        const first = parsed[0];
        return skipTitle && first?.type === 'heading' && first.level === 1 ? parsed.slice(1) : parsed;
    }, [source, skipTitle]);

    return <div className={'max-w-3xl'}>{renderBlocks(blocks, 'md', { slug, onOpenArticle, scrollRoot, isVisible }, false)}</div>;
};

export default MarkdownView;
